import React, { useState, useMemo } from "react";
import { 
  FileText, 
  Plus, 
  CheckCircle2, 
  Clock, 
  Search, 
  Filter, 
  ChevronRight, 
  Download, 
  ExternalLink, 
  MessageSquare, 
  Star,
  AlertCircle,
  MoreVertical,
  X,
  Send,
  User,
  BookOpen
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import InputField from "../../components/common/InputField";

// --- Dummy Data ---
const DUMMY_SUBMISSIONS = [
  {
    id: "SUB-001",
    student: "Alex Thompson",
    avatar: "https://i.pravatar.cc/150?u=alex",
    course: "Modern Web Design Masterclass",
    assignment: "Module 3: Responsive Layouts",
    submittedAt: "Oct 24, 2023 • 10:30 AM",
    status: "Pending",
    grade: null,
    file: "responsive_design_final.fig"
  },
  {
    id: "SUB-002",
    student: "Sarah Chen",
    avatar: "https://i.pravatar.cc/150?u=sarah",
    course: "Advanced Interactions",
    assignment: "Task 2: Framer Motion Sprung",
    submittedAt: "Oct 23, 2023 • 04:15 PM",
    status: "Graded",
    grade: "95/100",
    file: "interaction_v2.zip"
  },
  {
    id: "SUB-003",
    student: "James Wilson",
    avatar: "https://i.pravatar.cc/150?u=james",
    course: "Fullstack UI/UX Career Track",
    assignment: "Final Project: E-commerce UX",
    submittedAt: "Oct 22, 2023 • 09:00 AM",
    status: "Overdue",
    grade: null,
    file: null
  },
  {
    id: "SUB-004",
    student: "Emily Rodriguez",
    avatar: "https://i.pravatar.cc/150?u=emily",
    course: "Modern Web Design Masterclass",
    assignment: "Module 3: Responsive Layouts",
    submittedAt: "Oct 21, 2023 • 11:20 AM",
    status: "Pending",
    grade: null,
    file: "portfolio_layout.pdf"
  },
  {
    id: "SUB-005",
    student: "Marcus Ng",
    avatar: "https://i.pravatar.cc/150?u=marcus",
    course: "JavaScript Mastery",
    assignment: "Project 1: Weather API",
    submittedAt: "Oct 20, 2023 • 02:45 PM",
    status: "Graded",
    grade: "88/100",
    file: "weather_app.js"
  }
];

const GlassCard = ({ children, className = "" }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

const SubmissionDetail = ({ submission, onClose }) => {
  const [grade, setGrade] = useState("");
  const [feedback, setFeedback] = useState("");

  return (
    <motion.div 
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className="cht-fixed cht-right-0 cht-top-0 cht-h-full cht-w-full md:cht-w-[500px] cht-bg-white cht-z-[200] cht-shadow-[-20px_0_60px_rgba(0,0,0,0.05)] cht-border-l cht-border-gray-50 cht-flex cht-flex-col"
    >
      <div className="cht-p-8 cht-border-b cht-border-gray-50 cht-flex cht-items-center cht-justify-between cht-bg-white/80 cht-backdrop-blur-md">
        <div className="cht-space-y-1">
          <h3 className="cht-text-xl cht-font-black cht-text-black cht-tracking-tight">Review Submission</h3>
          <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">ID: {submission.id}</p>
        </div>
        <button onClick={onClose} className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-bg-gray-50 cht-rounded-full hover:cht-bg-gray-100 transition-colors">
          <X size={20} />
        </button>
      </div>

      <div className="cht-flex-1 cht-overflow-y-auto cht-p-8 cht-space-y-10">
        {/* Student Profile Info */}
        <div className="cht-flex cht-items-center cht-gap-4">
          <div className="cht-w-14 cht-h-14 cht-rounded-2xl cht-overflow-hidden">
            <img src={submission.avatar} alt={submission.student} className="cht-w-full cht-h-full cht-object-cover" />
          </div>
          <div>
            <h4 className="cht-text-lg cht-font-black cht-text-gray-900">{submission.student}</h4>
            <p className="cht-text-xs cht-font-bold cht-text-[#3e57da]">{submission.course}</p>
          </div>
        </div>

        {/* Assignment Info */}
        <div className="cht-space-y-4">
          <h4 className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-ml-1">Submission Details</h4>
          <div className="cht-p-6 cht-bg-gray-50/50 cht-rounded-3xl cht-border cht-border-gray-50 cht-space-y-4">
            <div>
              <p className="cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest cht-mb-1">Assignment</p>
              <p className="cht-text-sm cht-font-bold cht-text-gray-900">{submission.assignment}</p>
            </div>
            <div>
              <p className="cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest cht-mb-1">Submitted Date</p>
              <p className="cht-text-sm cht-font-bold cht-text-gray-900">{submission.submittedAt}</p>
            </div>
            {submission.file && (
              <div className="cht-pt-4">
                <Button variant="primary" className="cht-w-full cht-rounded-2xl" leftIcon={<Download size={18} />}>
                  Download {submission.file}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Grading Form */}
        <div className="cht-space-y-6">
          <h4 className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-ml-1">Grading & Feedback</h4>
          <div className="cht-space-y-4">
            <InputField 
              label="Grade (out of 100)" 
              placeholder="e.g. 95" 
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
            />
            <div className="cht-space-y-2">
              <label className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-ml-2">Feedback</label>
              <textarea 
                className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-2xl cht-p-6 cht-text-sm cht-font-medium cht-min-h-[150px] focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
                placeholder="Provide constructive feedback..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="cht-p-8 cht-border-t cht-border-gray-50 cht-flex cht-gap-4">
        <Button variant="ghost" onClick={onClose} className="cht-flex-1">Discard</Button>
        <Button variant="primary" className="cht-flex-1 cht-rounded-2xl" leftIcon={<CheckCircle2 size={18} />}>Submit Grade</Button>
      </div>
    </motion.div>
  );
};

const InstructorAssignments = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const filteredSubmissions = useMemo(() => {
    return DUMMY_SUBMISSIONS.filter(sub => {
      const matchesSearch = sub.student.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            sub.assignment.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === "All" || sub.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, filterStatus]);

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">Assignments</h1>
          <p className="cht-text-gray-500 cht-font-medium">Review and grade student submissions across your courses.</p>
        </div>
        <Button variant="primary" leftIcon={<Plus size={18} />} className="cht-rounded-2xl">
          Create Assignment
        </Button>
      </header>

      {/* Metrics Row */}
      <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-3 cht-gap-6">
        {[
          { label: "Pending Review", value: "12", icon: Clock, color: "amber" },
          { label: "Graded This Week", value: "48", icon: CheckCircle2, color: "green" },
          { label: "Completion Rate", value: "84%", icon: Star, color: "blue" }
        ].map((stat, i) => (
          <div key={i} className="cht-bg-white cht-p-8 cht-rounded-[32px] cht-border cht-border-gray-50 cht-shadow-sm cht-flex cht-items-center cht-gap-6">
            <div className={`cht-w-14 cht-h-14 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-bg-${stat.color}-50 cht-text-${stat.color}-600`}>
              <stat.icon size={24} />
            </div>
            <div>
              <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">{stat.label}</p>
              <h4 className="cht-text-2xl cht-font-black cht-text-gray-900">{stat.value}</h4>
            </div>
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-50 cht-shadow-sm">
        <div className="cht-relative cht-flex-1 cht-w-full">
          <Search className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" size={18} />
          <input 
            type="text" 
            placeholder="Search student or assignment..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-3.5 cht-pl-12 cht-pr-4 cht-text-sm cht-font-bold focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
          />
        </div>
        <div className="cht-flex cht-items-center cht-gap-2 cht-w-full md:cht-w-auto">
          {["All", "Pending", "Graded", "Overdue"].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`cht-flex-1 md:cht-flex-none cht-px-6 cht-py-3 cht-rounded-xl cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-transition-all ${
                filterStatus === status ? "cht-bg-[#3e57da] cht-text-white" : "cht-bg-gray-50 cht-text-gray-400 hover:cht-bg-gray-100"
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Submission List */}
      <GlassCard className="cht-rounded-[40px] cht-overflow-hidden">
        <div className="cht-overflow-x-auto">
          <table className="cht-w-full">
            <thead>
              <tr className="cht-border-b cht-border-gray-50">
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Student</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Assignment</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Date Submitted</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Status</th>
                <th className="cht-px-8 cht-py-6 cht-text-right cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="cht-divide-y cht-divide-gray-50">
              {filteredSubmissions.map((sub) => (
                <tr 
                  key={sub.id} 
                  className="hover:cht-bg-gray-50/50 cht-transition-colors cht-cursor-pointer group"
                  onClick={() => setSelectedSubmission(sub)}
                >
                  <td className="cht-px-8 cht-py-6">
                    <div className="cht-flex cht-items-center cht-gap-4">
                      <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-overflow-hidden">
                        <img src={sub.avatar} alt={sub.student} className="cht-w-full cht-h-full cht-object-cover" />
                      </div>
                      <h4 className="cht-text-sm cht-font-bold cht-text-gray-900">{sub.student}</h4>
                    </div>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <div className="cht-space-y-1">
                      <p className="cht-text-sm cht-font-bold cht-text-gray-900 cht-line-clamp-1">{sub.assignment}</p>
                      <p className="cht-text-[10px] cht-font-bold cht-text-[#3e57da] cht-uppercase cht-tracking-widest">{sub.course}</p>
                    </div>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <p className="cht-text-xs cht-font-medium cht-text-gray-500">{sub.submittedAt}</p>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <span className={`cht-px-3 cht-py-1.5 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase cht-tracking-widest ${
                      sub.status === 'Graded' ? 'cht-bg-green-100 cht-text-green-600' : 
                      sub.status === 'Pending' ? 'cht-bg-amber-100 cht-text-amber-600' : 
                      'cht-bg-rose-100 cht-text-rose-600'
                    }`}>
                      {sub.status === 'Graded' ? sub.grade : sub.status}
                    </span>
                  </td>
                  <td className="cht-px-8 cht-py-6 cht-text-right">
                    <div className="cht-flex cht-items-center cht-justify-end cht-gap-2">
                      <Button variant="ghost" size="sm" className="cht-w-10 cht-h-10 cht-p-0 cht-rounded-xl hover:cht-bg-gray-100">
                        <ExternalLink size={16} />
                      </Button>
                      <button className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-text-gray-300 hover:cht-text-[#3e57da] transition-colors">
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredSubmissions.length === 0 && (
            <div className="cht-py-20 cht-text-center">
              <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-mx-auto cht-mb-4 cht-text-gray-300">
                <FileText size={32} />
              </div>
              <h3 className="cht-text-lg cht-font-black cht-text-gray-900">No submissions found</h3>
              <p className="cht-text-sm cht-text-gray-400 cht-font-medium">Adjust your filters or search terms.</p>
            </div>
          )}
        </div>
      </GlassCard>

      {/* Side-over Detail View */}
      <AnimatePresence>
        {selectedSubmission && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="cht-fixed cht-inset-0 cht-bg-slate-900/60 cht-backdrop-blur-md cht-z-[150]"
              onClick={() => setSelectedSubmission(null)}
            />
            <SubmissionDetail 
              submission={selectedSubmission} 
              onClose={() => setSelectedSubmission(null)} 
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InstructorAssignments;
